import { baseUrl } from './api.js';

/**
 * Krok AS2 bod 6 — Nastavení -> „AI asistent a konektory". Stejny vzor jako
 * `mcp-approvals-api.ts`: raw `fetch` (routy jsou nove, jeste nejsou
 * v generovanem `@reos/api-client` schematu).
 */

export type AiAssistantProvider = 'off' | 'claude' | 'gpt';
/** Krok KR1 — kdo plati volani AI ('off' vychozi, vlastni klic, nebo kredity REOS). */
export type AiKeySource = 'off' | 'tenant_key' | 'app_credits';

export interface TenantSettingsDto {
  readonly tenantId: string;
  readonly brokerReadScope: 'tenant' | 'office';
  readonly costAlertPct: number;
  readonly aiAssistant: AiAssistantProvider;
  readonly aiKeySource: AiKeySource;
  /** Krok SR8b — IP rozsahy povolene pro prihlaseni ([] = bez omezeni). */
  readonly allowedIpRanges: readonly string[];
  /** Krok P1EXP1 — smi kancelar exportovat na portaly ostre (vychozi false). */
  readonly portalLiveExport: boolean;
  /** Krok SCHV1 — vyzadovat souhlas majitele pred exportem (vychozi false). */
  readonly requireOwnerApproval: boolean;
  /** Krok UI8 — IP aktualniho pozadavku (pro varovani "nezamkni se"). */
  readonly currentIp: string;
  readonly updatedAt: string;
}

export async function getTenantSettings(): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

export async function setAiAssistant(aiAssistant: AiAssistantProvider): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ aiAssistant }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

export async function setAiKeySource(aiKeySource: AiKeySource): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ aiKeySource }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

/**
 * Krok SCHV1 bod 4 — Portály → „vyžadovat schválení majitelem". Zapnuto =
 * nabídka odejde na externí portál teprve po souhlasu majitele (brána je na
 * serveru, v @reos/listing); vypnuto (výchozí) = makléř dostane u zakázky
 * jen upozornění a může ho vědomě přeskočit.
 */
export async function setRequireOwnerApproval(requireOwnerApproval: boolean): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ requireOwnerApproval }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

/**
 * Krok P1EXP1 — Portály → ostrý export kanceláře. Přepínač NEDÁVÁ přístupy
 * k portálu (ty zadává člověk v Portálech jako šifrované credentials), jen
 * povoluje režim LIVE; vypnutí platí okamžitě i pro už zařazené operace.
 */
export async function setPortalLiveExport(portalLiveExport: boolean): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ portalLiveExport }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

/**
 * Krok POB1 bod 2 — Nastavení → Pobočky: rozsah čtení makléře.
 * `'tenant'` = makléř vidí celou kancelář, `'office'` = jen svou pobočku
 * (serverové zúžení dělá `brokerOfficeListScope` v `@reos/policy`, tohle je
 * JEN přepínač nastavení — žádné druhé rozhodovací místo v prohlížeči).
 */
export async function setBrokerReadScope(brokerReadScope: 'tenant' | 'office'): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ brokerReadScope }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

/** Krok SR8b/UI8 — Nastavení → Zabezpečení: uloží seznam IPv4/CIDR rozsahů. */
export async function setAllowedIpRanges(allowedIpRanges: readonly string[]): Promise<TenantSettingsDto> {
  const res = await fetch(`${baseUrl}/v1/tenant-settings`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ allowedIpRanges }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TenantSettingsDto;
}

export interface McpConnectionDto {
  readonly familyId: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly userId: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
}

export async function listMcpConnections(): Promise<readonly McpConnectionDto[]> {
  const res = await fetch(`${baseUrl}/v1/mcp/connections`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { items: McpConnectionDto[] };
  return data.items;
}

export async function disconnectMcpConnection(familyId: string): Promise<void> {
  const res = await fetch(`${baseUrl}/v1/mcp/connections/${encodeURIComponent(familyId)}/disconnect`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/** Verejna adresa MCP konektoru - nenastavena v dev/preview bez vlastni domeny (zadna hadana URL). */
export function mcpConnectorUrl(): string | undefined {
  const raw = (import.meta as unknown as { env: Record<string, string | undefined> }).env['VITE_REOS_MCP_GATEWAY_URL'];
  return raw === undefined || raw === '' ? undefined : `${raw.replace(/\/$/, '')}/mcp`;
}
